import axios from 'axios';
import { formatDate } from '../helper/utils';
import { APP_NAME } from '../utils/constants';

// Every gs-intg response is wrapped as { data: <payload> }.
const withSlash = (url) => {
  if (!url) return '';
  return url.endsWith('/') ? url : `${url}/`;
};

export const GS_BASE_URL = withSlash(process.env.REACT_APP_BASE_URL);
const WA_REPORT_BASE_URL = withSlash(process.env.REACT_APP_WA_REPORT_BASE_URL);

export const unwrap = (res) => res.data?.data ?? res.data;

// unwrapNullable: distinguishes {"data":null} (missing org) from an absent key via 'in'.
const unwrapNullable = (res) => {
  const payload = res.data;
  return payload && typeof payload === 'object' && 'data' in payload ? payload.data : payload;
};

export async function getOrg(orgId) {
  try {
    const res = await axios.get(`${GS_BASE_URL}integrations/org/${orgId}`);
    const data = unwrapNullable(res);
    return data && Object.keys(data).length ? data : null;
  } catch (err) {
    if (err.response?.status === 404) return null;
    throw err;
  }
}

export async function createOrg(orgId) {
  const res = await axios.post(`${GS_BASE_URL}integrations/org`, {
    id: orgId,
    integrationId: APP_NAME,
    channels: [],
    meta: { createdVia: 'bloomreach-ui' },
  });
  return unwrap(res);
}

export async function updateOrg(orgId, { meta, auth, config } = {}) {
  const res = await axios.put(`${GS_BASE_URL}integrations/org/${orgId}`, { meta, auth, config });
  return unwrap(res);
}

export async function addChannel(orgId, channel) {
  const res = await axios.post(`${GS_BASE_URL}integrations/org/${orgId}/channel`, channel);
  return unwrap(res);
}

export async function updateChannel(orgId, channelId, channel) {
  const res = await axios.put(`${GS_BASE_URL}integrations/org/${orgId}/channel/${channelId}`, channel);
  return unwrap(res);
}

export async function deleteChannel(orgId, channelId) {
  const res = await axios.delete(`${GS_BASE_URL}integrations/org/${orgId}/channel/${channelId}`);
  return unwrap(res);
}

export async function validateEnterprise(userId, password) {
  const res = await axios.get(`${GS_BASE_URL}whatsapp/enterprise/validate`, {
    params: { userid: userId, password },
  });
  return unwrap(res);
}

export async function subscribeInbound(orgId, channelId) {
  const res = await axios.post(`${GS_BASE_URL}whatsapp/enterprise/subscription`, {
    orgId,
    channelId,
    integrationId: APP_NAME,
  });
  return unwrap(res);
}

// Raw, unfiltered array — the Message page filters by ENABLED/LOCATION/CAROUSEL.
// status: ENABLED PENDING REJECTED DISABLED ARCHIVED ALL — server defaults to ENABLED when omitted.
export async function getTemplates(orgId, channelId, status) {
  // Omit params entirely when status is undefined so the URL stays byte-identical for existing callers.
  const config = status === undefined ? undefined : { params: { status } };
  const res = await axios.get(`${GS_BASE_URL}whatsapp/enterprise/${orgId}/channel/${channelId}`, config);
  // This route double-wraps ({data:{data:[...]}}); dig through .data until it's an array.
  let payload = res.data;
  while (payload && !Array.isArray(payload) && payload.data !== undefined) {
    payload = payload.data;
  }
  return Array.isArray(payload) ? payload : [];
}

// The consent categories of the Bloomreach project behind a sending number: [{ id, name }], where id is the exact
// consent_category value. gs-intg answers 400, 404 (no Bloomreach credentials for that number) or 502, which reject here.
export async function getConsentCategories(orgId, number) {
  const res = await axios.get(`${GS_BASE_URL}integrations/org/${orgId}/bloomreach/consent-categories`, {
    params: { number },
  });
  const list = unwrap(res);
  return Array.isArray(list) ? list : [];
}

// Stores the Bloomreach credentials of one sending number: { number, url, projectToken, groupKey }. gs-intg checks the
// group key against Bloomreach before it writes anything, and rejects here with a display string in
// err.response.data.message: 400 (bad input, key refused, not a channel, private keys sent), 404 (no org), 409 (already
// set up) or 502 (could not reach Bloomreach). The reply is { number, url, projectToken }, never the key.
export async function setupBloomreachCredentials(orgId, body) {
  const res = await axios.post(`${GS_BASE_URL}integrations/org/${orgId}/bloomreach/credentials`, body);
  return unwrap(res);
}

export async function uploadMedia(file) {
  const body = new FormData();
  body.append('file', file);
  try {
    const res = await axios.post(`${GS_BASE_URL}whatsapp/enterprise/uploadmedia`, body);
    return unwrap(res);
  } catch {
    // Enterprise upload route rejects some files; fall back to the selfserve twin endpoint.
    // Both routes now funnel through gs-intg's same S3 upload (ImageKitService.invokeImageKitUpload,
    // since CUSTENG-10097), so this fallback no longer helps when S3 itself is the failure — the
    // caller (Message/index.js) is what turns a rejection here into a visible error.
    const res = await axios.post(`${GS_BASE_URL}whatsapp/selfserve/uploadmedia`, body);
    return unwrap(res);
  }
}

// Dashboard/DLR calls: gated by ENABLE_DASHBOARD; empty until DLRs reach BigQuery.
export async function getSummary(orgId, data, wabaNumber) {
  const res = await axios(
    `${WA_REPORT_BASE_URL}dlr/summary?orgId=${orgId}&integrationId=${APP_NAME}&wabaNo=${wabaNumber}&startDate=${formatDate(data?.dateRange?.startDate)}&endDate=${formatDate(data?.dateRange?.endDate)}`
  );
  return res?.data?.data;
}

export async function getTableData(orgId, data, wabaNumber, page, limit) {
  const res = await axios({
    method: 'get',
    url: `${WA_REPORT_BASE_URL}dlr/campaign-summary?orgId=${orgId}&integrationId=${APP_NAME}&startDate=${formatDate(data?.dateRange?.startDate)}&endDate=${formatDate(data?.dateRange?.endDate)}&wabaNo=${wabaNumber}&identifier=programId&page=${page}&limit=${limit}`,
  });
  const result = res?.data?.data;
  return {
    data: result?.campaigns,
    meta: {
      totalRecords: result?.totalCampaigns,
      limit,
      offset: page,
    },
  };
}

// Shared by getFilteredTableData/getExportData below — both build the exact same
// dlr/campaign-summary URL, differing only in which limit/page they pass and how the response
// is unwrapped.
function buildCampaignSummaryUrl(orgId, data, wabaNumber, limit, page) {
  const programs = data?.programs;
  let url = `${WA_REPORT_BASE_URL}dlr/campaign-summary?orgId=${orgId}&integrationId=${APP_NAME}&wabaNo=${wabaNumber}&startDate=${formatDate(data?.dateRange?.startDate)}&endDate=${formatDate(data?.dateRange?.endDate)}&identifier=programId&limit=${limit}&page=${page}`;
  if (programs?.length > 0) url += `&identifierIdList=${programs}`;
  return url;
}

export async function getFilteredTableData(orgId, data, wabaNumber, limit) {
  const res = await axios(buildCampaignSummaryUrl(orgId, data, wabaNumber, limit, data?.page - 1));
  return {
    data: res?.data?.data?.campaigns,
    meta: {
      totalRecords: res?.data?.data?.totalCampaigns,
      limit,
      offset: data?.page - 1,
    },
  };
}

export async function getExportData(orgId, data, wabaNumber) {
  const res = await axios(buildCampaignSummaryUrl(orgId, data, wabaNumber, data?.offset, 0));
  return res?.data?.data?.campaigns;
}

export async function getIdentifiers(orgId, startDate, endDate, wabaNumber) {
  const res = await axios({
    method: 'get',
    url: `${WA_REPORT_BASE_URL}dlr/identifiers?orgId=${orgId}&integrationId=${APP_NAME}&startDate=${formatDate(startDate)}&endDate=${formatDate(endDate)}&wabaNo=${wabaNumber}&identifier=programId`,
  });
  const data = [];
  res.data?.data?.programId?.forEach((element) => {
    data.push({ label: element, value: element });
  });
  return data;
}
